import { useState, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useUserRole, useRequireRole } from '@/hooks/useRoleGuard'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Download, Users, Building2, Activity, BarChart3 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import Logo from '@/components/Logo'

interface WaitlistEntry {
  id: string
  email: string
  source: string
  created_at: string
  confirmed_at: string | null
  metadata: Record<string, any>
}

interface Partner {
  id: string
  company_name: string
  contact_name: string
  email: string
  phone: string | null
  website: string | null
  vertical: string
  avg_ticket_aed: number | null
  city: string | null
  notes: string | null
  created_at: string
}

interface Event {
  id: string
  event_type: string
  email: string | null
  source: string | null
  user_agent: string | null
  ip_address: string | null
  metadata: Record<string, any>
  created_at: string
}
// ── Checkout Intelligence Tab ────────────────────────────────
function CheckoutIntelligenceTab() {
  const [stats, setStats] = useState<{
    todayCount: number;
    acceptanceRate: number;
    providerDist: Record<string, number>;
    abandonedStep: string;
  } | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    async function loadStats() {
      try {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);

        const { data: allEvents, error } = await (supabase
          .from('checkout_events' as any)
          .select('event, metadata, created_at')
          .gte('created_at', todayStart.toISOString())
          .order('created_at', { ascending: false })
          .limit(1000) as any);

        if (error) throw error;

        const events = (allEvents ?? []) as Array<{
          event: string;
          metadata: Record<string, any>;
          created_at: string;
        }>;

        const todayCount = events.length;

        // Acceptance rate
        const selections = events.filter((e) => e.event === 'provider_selected');
        const followed = selections.filter(
          (e) => e.metadata?.followedRecommendation === true
        );
        const acceptanceRate =
          selections.length > 0
            ? Math.round((followed.length / selections.length) * 100)
            : 0;

        // Provider distribution
        const providerDist: Record<string, number> = {};
        selections.forEach((e) => {
          const p = (e.metadata?.provider as string) ?? 'unknown';
          providerDist[p] = (providerDist[p] ?? 0) + 1;
        });

        // Most abandoned step (most common event before abandon)
        const abandoned = events.filter((e) => e.event === 'checkout_abandoned');
        const abandonedStep =
          abandoned.length > 0 ? `${abandoned.length} abandonments today` : 'None today';

        setStats({ todayCount, acceptanceRate, providerDist, abandonedStep });
      } catch (err) {
        console.error('Checkout stats error:', err);
      } finally {
        setLoadingStats(false);
      }
    }
    loadStats();
  }, []);

  if (loadingStats) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Loading checkout intelligence…
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No checkout data available yet.
        </CardContent>
      </Card>
    );
  }

  const providerEntries = Object.entries(stats.providerDist).sort(
    (a, b) => b[1] - a[1]
  );
  const maxSelections = providerEntries.length > 0 ? providerEntries[0][1] : 1;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Checkout Intelligence</CardTitle>
        <CardDescription>
          Phase 2 ML training data — today's checkout behaviour
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* KPI cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border p-4">
            <p className="text-sm text-muted-foreground">Events Today</p>
            <p className="text-2xl font-bold font-mono">{stats.todayCount}</p>
          </div>
          <div className="rounded-lg border p-4">
            <p className="text-sm text-muted-foreground">Recommendation Acceptance</p>
            <p className="text-2xl font-bold font-mono">{stats.acceptanceRate}%</p>
          </div>
          <div className="rounded-lg border p-4">
            <p className="text-sm text-muted-foreground">Abandonments</p>
            <p className="text-2xl font-bold font-mono">{stats.abandonedStep}</p>
          </div>
          <div className="rounded-lg border p-4">
            <p className="text-sm text-muted-foreground">Providers Selected</p>
            <p className="text-2xl font-bold font-mono">{providerEntries.length}</p>
          </div>
        </div>

        {/* Provider distribution bar chart */}
        <div>
          <h3 className="text-sm font-semibold mb-3">Provider Selection Distribution</h3>
          {providerEntries.length === 0 ? (
            <p className="text-sm text-muted-foreground">No selections recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {providerEntries.map(([provider, count]) => (
                <div key={provider} className="flex items-center gap-3">
                  <span className="w-20 text-sm font-medium capitalize">{provider}</span>
                  <div className="flex-1 h-6 bg-muted rounded overflow-hidden">
                    <div
                      className="h-full bg-primary rounded transition-all"
                      style={{ width: `${(count / maxSelections) * 100}%` }}
                    />
                  </div>
                  <span className="w-8 text-sm font-mono text-right">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const Admin = () => {
  const { user, isLoading: authLoading, signOut } = useAuth()
  const { role, isLoading: roleLoading } = useUserRole()
  const { toast } = useToast()
  
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntry[]>([])
  const [partners, setPartners] = useState<Partner[]>([])
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)

  // Use role guard hook
  useRequireRole('admin');

  // Show loading while checking auth
  if (authLoading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <Logo />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated or not admin
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast({
        title: "Error signing out",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      // Load waitlist entries
      const { data: waitlistData, error: waitlistError } = await supabase
        .from('waitlist')
        .select('*')
        .order('created_at', { ascending: false })

      if (waitlistError) throw waitlistError
      setWaitlistEntries(waitlistData as WaitlistEntry[])

      // Load partners data
      const { data: partnersData, error: partnersError } = await supabase
        .from('partners')
        .select('*')
        .order('created_at', { ascending: false })

      if (partnersError) throw partnersError
      setPartners(partnersData as Partner[])

      // Load analytics events
      const { data: eventsData, error: eventsError } = await supabase
        .from('waitlist_analytics')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100)

      if (eventsError) throw eventsError
      setEvents(eventsData as Event[])

    } catch (error) {
      console.error('Error loading admin data:', error)
      toast({
        title: "Error loading data",
        description: "Failed to load admin data. Please check your connection.",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const exportWaitlistCSV = () => {
    const csvData = waitlistEntries.map(entry => ({
      Email: entry.email,
      Name: '', // We don't collect names in waitlist signup yet
      CreatedAt: new Date(entry.created_at).toLocaleDateString()
    }))

    downloadCSV(csvData, 'waitlist-entries.csv')
  }

  const exportPartnersCSV = () => {
    const csvData = partners.map(partner => ({
      CompanyName: partner.company_name,
      ContactName: partner.contact_name,
      Email: partner.email,
      Phone: partner.phone || '',
      Website: partner.website || '',
      Vertical: partner.vertical,
      AvgTicketAED: partner.avg_ticket_aed || '',
      City: partner.city || '',
      Notes: partner.notes || '',
      CreatedAt: new Date(partner.created_at).toLocaleDateString()
    }))

    downloadCSV(csvData, 'partner-applications.csv')
  }

  const exportEventsCSV = () => {
    const csvData = events.map(event => ({
      EventType: event.event_type,
      Email: event.email || '',
      Source: event.source || '',
      UserAgent: event.user_agent || '',
      IPAddress: event.ip_address || '',
      Metadata: JSON.stringify(event.metadata),
      CreatedAt: new Date(event.created_at).toLocaleDateString()
    }))

    downloadCSV(csvData, 'analytics-events.csv')
  }

  const downloadCSV = (data: any[], filename: string) => {
    const csvContent = convertToCSV(data)
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    toast({
      title: "Export successful",
      description: `${filename} has been downloaded.`,
    })
  }

  const convertToCSV = (data: any[]) => {
    if (data.length === 0) return ''
    
    const headers = Object.keys(data[0])
    const csvHeaders = headers.join(',')
    
    const csvRows = data.map(row =>
      headers.map(header => {
        const value = row[header]
        // Handle values that might contain commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`
        }
        return value
      }).join(',')
    )
    
    return [csvHeaders, ...csvRows].join('\n')
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <Logo />
          <p className="text-muted-foreground">Loading admin data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <Logo />
            <div>
              <h1 className="text-3xl font-bold text-foreground">Admin Dashboard</h1>
              <p className="text-muted-foreground">
                Manage waitlist entries, partner applications, and system events
              </p>
            </div>
          </div>
          <Button onClick={handleSignOut} variant="outline">
            Sign Out
          </Button>
        </div>

        <Tabs defaultValue="waitlist" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="waitlist" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Waitlist ({waitlistEntries.length})
            </TabsTrigger>
            <TabsTrigger value="partners" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Partners ({partners.length})
            </TabsTrigger>
            <TabsTrigger value="events" className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Events ({events.length})
            </TabsTrigger>
            <TabsTrigger value="transactions" className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Transactions
            </TabsTrigger>
            <TabsTrigger value="checkout-intel" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Checkout Intel
            </TabsTrigger>
          </TabsList>

          <TabsContent value="waitlist">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Waitlist Entries</CardTitle>
                    <CardDescription>
                      {waitlistEntries.length} total entries
                    </CardDescription>
                  </div>
                  <Button onClick={exportWaitlistCSV} variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {waitlistEntries.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No waitlist entries yet.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Created At</TableHead>
                        <TableHead>Confirmed</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {waitlistEntries.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="font-medium">{entry.email}</TableCell>
                          <TableCell>{entry.source}</TableCell>
                          <TableCell>{new Date(entry.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            {entry.confirmed_at ? (
                              <span className="text-green-600">
                                {new Date(entry.confirmed_at).toLocaleDateString()}
                              </span>
                            ) : (
                              <span className="text-yellow-600">Pending</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="partners">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Partner Applications</CardTitle>
                    <CardDescription>
                      {partners.length} total applications
                    </CardDescription>
                  </div>
                  <Button onClick={exportPartnersCSV} variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {partners.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No partner applications yet.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Vertical</TableHead>
                        <TableHead>Avg Ticket (AED)</TableHead>
                        <TableHead>Created At</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {partners.map((partner) => (
                        <TableRow key={partner.id}>
                          <TableCell className="font-medium">{partner.company_name}</TableCell>
                          <TableCell>{partner.contact_name}</TableCell>
                          <TableCell>{partner.email}</TableCell>
                          <TableCell>{partner.vertical}</TableCell>
                          <TableCell>
                            {partner.avg_ticket_aed ? `${partner.avg_ticket_aed.toLocaleString()}` : '—'}
                          </TableCell>
                          <TableCell>{new Date(partner.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="events">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>System Events</CardTitle>
                    <CardDescription>
                      Recent analytics events (last 100)
                    </CardDescription>
                  </div>
                  <Button onClick={exportEventsCSV} variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {events.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No events recorded yet.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Event Type</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>IP Address</TableHead>
                        <TableHead>Created At</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {events.map((event) => (
                        <TableRow key={event.id}>
                          <TableCell className="font-medium">{event.event_type}</TableCell>
                          <TableCell>{event.email || '—'}</TableCell>
                          <TableCell>{event.source || '—'}</TableCell>
                          <TableCell className="font-mono text-sm">{event.ip_address || '—'}</TableCell>
                          <TableCell>{new Date(event.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>Transaction Overview</CardTitle>
                <CardDescription>
                  Mock transaction data for demo purposes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-3 mb-6">
                  <div className="bg-primary/10 rounded-lg p-4">
                    <div className="text-sm text-muted-foreground mb-1">Total Transactions</div>
                    <div className="text-2xl font-bold text-primary">247</div>
                    <div className="text-xs text-muted-foreground mt-1">+12% vs last month</div>
                  </div>
                  <div className="bg-accent/10 rounded-lg p-4">
                    <div className="text-sm text-muted-foreground mb-1">Total Volume</div>
                    <div className="text-2xl font-bold text-accent">AED 1.2M</div>
                    <div className="text-xs text-muted-foreground mt-1">+18% vs last month</div>
                  </div>
                  <div className="bg-green-100 rounded-lg p-4">
                    <div className="text-sm text-muted-foreground mb-1">Completion Rate</div>
                    <div className="text-2xl font-bold text-green-700">89%</div>
                    <div className="text-xs text-muted-foreground mt-1">+3% vs last month</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold">Provider Breakdown</h3>
                  <div className="grid gap-3">
                    <div className="flex items-center justify-between p-3 bg-accent/5 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center">
                          <span className="text-accent font-semibold">T</span>
                        </div>
                        <div>
                          <div className="font-medium">Tabby</div>
                          <div className="text-sm text-muted-foreground">124 transactions</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">AED 580K</div>
                        <div className="text-sm text-muted-foreground">50%</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                          <span className="text-primary font-semibold">T</span>
                        </div>
                        <div>
                          <div className="font-medium">Tamara</div>
                          <div className="text-sm text-muted-foreground">87 transactions</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">AED 420K</div>
                        <div className="text-sm text-muted-foreground">35%</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                          <span className="text-green-700 font-semibold">C</span>
                        </div>
                        <div>
                          <div className="font-medium">Cashew</div>
                          <div className="text-sm text-muted-foreground">36 transactions</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">AED 200K</div>
                        <div className="text-sm text-muted-foreground">15%</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="checkout-intel">
            <CheckoutIntelligenceTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

export default Admin